/**
 * Runtime base path helper.
 *
 * The virtual-web app can be served either at:
 * - `/` (standalone on `virtual-sports.caribe7.local`)
 * - `/virtual-sports` (embedded behind sportsbook at `sports.caribe7.local/virtual-sports`)
 *
 * When embedded on a different origin (e.g. app.caribe7.dev loading the remote from
 * virtual-sports.caribe7.dev), the host does not serve the remote's static assets or
 * locales. We must load them from the remote's public URL (REACT_APP_REMOTE_PUBLIC_URL).
 */

/** Known host -> remote asset origin when virtual-web is loaded as MF remote. Used when REACT_APP_REMOTE_PUBLIC_URL is not set at build time. */
const EMBEDDED_ORIGIN_TO_REMOTE: Record<string, string> = {
  'https://app.caribe7.dev': 'https://virtual-sports.caribe7.dev',
  'http://app.caribe7.dev': 'http://virtual-sports.caribe7.dev',
  'https://app.caribe7.com': 'https://virtual-sports.caribe7.com',
  'http://app.caribe7.com': 'http://virtual-sports.caribe7.com',
  'http://app.caribe7.local': 'http://virtual-sports.caribe7.local',
  'http://localhost:8041': 'http://localhost:8031',
};

/** When set (e.g. in dev/prod as MF remote), load assets and i18n from this origin. No trailing slash. Optional: when unset we use EMBEDDED_ORIGIN_TO_REMOTE or pathname. */
export function getRemoteAssetOrigin(): string {
  if (typeof window === 'undefined') return '';
  let url = String(process.env.REACT_APP_REMOTE_PUBLIC_URL ?? '').trim();
  if (!url && window.location?.pathname?.startsWith('/virtual-sports')) {
    const origin = window.location?.origin ?? '';
    url = EMBEDDED_ORIGIN_TO_REMOTE[origin] ?? '';
  }
  return url.replace(/\/+$/, '');
}

export function getRuntimeBasePath(): string {
  if (typeof window === 'undefined') return '';
  const p = String(window.location?.pathname ?? '/');
  return p.startsWith('/virtual-sports') ? '/virtual-sports' : '';
}

export function withBasePath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const remoteOrigin = getRemoteAssetOrigin();
  const base = getRuntimeBasePath();
  // When embedded and we have a remote public URL, assets must load from the remote origin.
  if (remoteOrigin && base) return `${remoteOrigin}${normalized}`;
  return `${base}${normalized}`;
}

/** Base-aware route paths so navigations stay under /virtual-sports when embedded in platform-web. */
export function getRoutes(): { HOME: string; NO_SESSION: string } {
  const base = getRuntimeBasePath();
  return {
    HOME: base ? base : '/',
    NO_SESSION: base ? `${base}/no-session` : '/no-session',
  };
}

